/**
 * Product card that lifts out of the floor with reflection + glossy highlight.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  brandName, pickMediaSrc,
} from "../../reel/sceneShared.js";

export const productCardLift: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const title = inputs.text ?? brandName;
  const sub = inputs.subtext ?? "";
  const badge = String(inputs.extras?.badge ?? "NEU").trim();
  const isVertical = ctx.format === "9:16";
  const media = pickMediaSrc(inputs.media);

  const cardW = isVertical ? 720 : 640;
  const cardH = Math.round(cardW * 1.15);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const cardId = `${clipPrefix}-card`;
  const cardImg = `${clipPrefix}-img`;
  const reflId = `${clipPrefix}-refl`;
  const gloss = `${clipPrefix}-gloss`;
  const titleId = `${clipPrefix}-title`;
  const subId = `${clipPrefix}-sub`;
  const badgeId = `${clipPrefix}-badge`;
  const haloId = `${clipPrefix}-halo`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const inner = media
    ? `<img id="${cardImg}" src="${escapeHtml(media)}" alt="product" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" />`
    : `<div id="${cardImg}" style="position:absolute;inset:0;background:linear-gradient(165deg, ${accent} 0%, ${withAlpha(accent, 0.4)} 60%, ${themeColors.bg} 100%);display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardW * 0.32}px;letter-spacing:-6px;">${escapeHtml(title.charAt(0).toUpperCase())}</div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px;padding:64px;perspective:1800px;">
    <div style="position:relative;display:flex;justify-content:center;">
      <div id="${haloId}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:${cardW * 1.4}px;height:${cardH * 1.1}px;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(34px);"></div>
      <div id="${cardId}" style="position:relative;width:${cardW}px;height:${cardH}px;border-radius:48px;overflow:hidden;background:#0e1117;box-shadow:0 60px 120px rgba(0,0,0,0.55), 0 0 80px ${withAlpha(accent, 0.5)}, inset 0 1px 0 rgba(255,255,255,0.08);border:2px solid ${withAlpha(themeColors.fg, 0.18)};">
        ${inner}
        <div id="${gloss}" style="position:absolute;inset:0;background:linear-gradient(115deg, ${withAlpha("#ffffff", 0.25)} 0%, transparent 30%, transparent 60%, ${withAlpha("#ffffff", 0.08)} 100%);mix-blend-mode:screen;pointer-events:none;"></div>
        <div id="${badgeId}" style="position:absolute;top:18px;right:18px;padding:8px 16px;border-radius:9999px;background:${accent};color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.4}px;letter-spacing:2.5px;text-transform:uppercase;box-shadow:0 0 24px ${withAlpha(accent, 0.65)};">${escapeHtml(badge)}</div>
      </div>
      <div id="${reflId}" style="position:absolute;left:50%;bottom:-${cardH * 0.4}px;transform:translateX(-50%) scaleY(-1);width:${cardW}px;height:${cardH * 0.45}px;border-radius:48px;background:linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%);mask:linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%);-webkit-mask:linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%);overflow:hidden;opacity:0.35;">
        <div style="position:absolute;inset:0;background:linear-gradient(180deg, ${withAlpha(accent, 0.45)} 0%, transparent 100%);"></div>
      </div>
    </div>
    <div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};text-align:center;line-height:1.05;">${escapeHtml(title)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.95}px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:760px;text-wrap:balance;">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const init = [
    gsapSetLine(`#${cardId}`, { opacity: 0, y: 120, rotationX: 24, scale: 0.94, transformOrigin: "50% 100%" }),
    gsapSetLine(`#${reflId}`, { opacity: 0 }),
    gsapSetLine(`#${haloId}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${gloss}`, { x: -cardW, opacity: 0.6 }),
    gsapSetLine(`#${titleId}`, { opacity: 0, y: 18 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${badgeId}`, { opacity: 0, scale: 0.5, rotation: -12 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${haloId}`, { opacity: 0.85, scale: 1, duration: 0.8, ease: "raw:\"power2.out\"" }, 0.0),
    t.to(`#${cardId}`, { opacity: 1, y: 0, rotationX: 0, scale: 1, duration: 1.1, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${reflId}`, { opacity: 0.4, duration: 0.8, ease: "raw:\"power2.out\"" }, 0.7),
    t.to(`#${gloss}`, { x: cardW, duration: Math.max(1.4, clipDurationSec - 1.0), ease: "raw:\"sine.inOut\"" }, 0.6),
    t.to(`#${badgeId}`, { opacity: 1, scale: 1, rotation: 0, duration: 0.5, ease: "raw:\"back.out(2)\"" }, 0.8),
    t.to(`#${titleId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.95),
    t.to(`#${cardId}`, { y: -8, duration: Math.max(1.4, clipDurationSec - 2.2), ease: "raw:\"sine.inOut\"" }, 1.5),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.1));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
